import { Injectable } from '@angular/core';
import { EntityChildsContext } from 'app/private/entity/entityWithChilds/model/EntityChildsContext';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs/Rx';
import {
  Marketplace,
  MarketPlaceCategory,
  MarketPlaceBusinessLogic,
  MarketPlaceChannelReportConfiguration,
  MarketPlaceConnections,
  MarketPlaceDataLogic,
  MarketPlaceNonTransactionalFlow,
  MarketPlaceStandardDictionary,
  MarketPlaceStandardFlow,
  MarketPlaceStandardProductEmail,
  MarketPlaceStandardProductReport,
  MarketPlaceViewEngine,
} from '../model/classes/marketplace.class';
import { FullContextStandardFlowSubscribe } from '../model/interfaces/fullContextModel.interface';
import { SxHttpService } from './sxHttp.service';

export const MARKETPLACE_ITEMTYPE_AllOfThem = 'ALL';
export const MARKETPLACE_ITEMTYPE_StandardFlow = 'STANDARDFLOW';
export const MARKETPLACE_ITEMTYPE_NonTransactionalFlow = 'NONTRANSACTIONALFLOW';
export const MARKETPLACE_ITEMTYPE_BusinessLogic = 'BUSINESSLOGIC';
export const MARKETPLACE_ITEMTYPE_DataLogic = 'DATALOGIC';
export const MARKETPLACE_ITEMTYPE_ChannelReportConfiguration =
  'CHANNELREPORTCONFIGURATION';
export const MARKETPLACE_ITEMTYPE_StandardProductReport = 'PRODUCTREPORT';
export const MARKETPLACE_ITEMTYPE_StandardProductEmail = 'PRODUCTEMAIL';
export const MARKETPLACE_ITEMTYPE_ViewEngines = 'VIEWENGINE';
export const MARKETPLACE_ITEMTYPE_Connections = 'CONNECTION';
export const MARKETPLACE_ITEMTYPE_StandardDictionary = 'STANDARDDICTIONARY';

export interface IResponse {
  marketplaceItems: Marketplace[];
  alloweditems: string[];
}

@Injectable()
export class MarketplaceService {
  private serviceUrl = 'api/Marketplace';
  private StandardFlowUrl = 'api/stardardProductFlow';
  private businessLogicSubscriptionSrv = 'api/BussinesLogicSubscription';

  private categories = new ReplaySubject<any[]>();
  public categoriesTypes = this.categories.asObservable();

  constructor(private sxHttp: SxHttpService) {}

  getMarketplaces(
    targetTypes: string[],
    targetOrganizations: string[] = null
  ): Observable<any> {
    const req = {
      TargetTypes: targetTypes,
      TargetOrganizations: targetOrganizations,
      AnyOrganization: targetOrganizations === null,
    };
    return this.sxHttp
      .post(`${this.serviceUrl}/GetAll`, req)
      .map((response) => this.setModel(response.json().data));
  }

  //#region Standard Flow
  getStandardProductFlowsForUnbinded(
    codSistema,
    codNegocio,
    codProducto,
    documentKind,
    standardKey
  ): Observable<Marketplace[]> {
    const url = `${this.StandardFlowUrl}/GetUnbindedByContext/${codSistema}/${codNegocio}/${codProducto}/${documentKind}/${standardKey}`;
    return this.sxHttp
      .get(url)
      .map((response) => this.setModel(response.json().data))
      .catch(this.handleError);
  }

  subscribeStandardProductFlow(
    organizatinCode: string,
    codSistema: string,
    codNegocio: string,
    codProducto: string,
    documentKind: string,
    standardKey: string,
    codFlows: any[]
  ) {
    const url = `${this.StandardFlowUrl}/RelateToContext`;
    const req: FullContextStandardFlowSubscribe = {
      organizationCode: organizatinCode,
      systemCode: codSistema,
      businessCode: codNegocio,
      productCode: codProducto,
      codFlow: codFlows,
      dictionaryKey: standardKey,
      documentKind: documentKind,
    };

    return this.sxHttp.post(url, req).toPromise().catch(this.handleError);
  }
  //#endregion

  // #region Business Logic
  
  getAllWithContext(
    organizationCode: string,
    subscriptionCode: string,
    channelCode: string,
    productCode: string
  ): Observable<Marketplace[]> {
    const context: EntityChildsContext = {
      organizationCode,
      subscriptionCode,
      channelCode,
      productCode,
    };
    return this.sxHttp
      .post(this.businessLogicSubscriptionSrv + '/GetBusinessLogic', context)
      .map((response) => this.setModel(response.json().data));
  }
  
  businessSubscription(businessLogicSubscription): Observable<Boolean> {
    return this.sxHttp
      .post(
        `${this.businessLogicSubscriptionSrv}/Subscription`,
        businessLogicSubscription
      )
      .map((response) => {
        return response.json().data;
      });
  }

  //#endregion

  setModel(response: IResponse): Marketplace[] {
    let marketplace: Marketplace[] = [];
    this.categories.next(response.alloweditems);
    response.marketplaceItems.forEach((model) => {
      switch (model.type) {
        case MARKETPLACE_ITEMTYPE_StandardFlow:
          marketplace.push(
            new MarketPlaceStandardFlow(model as MarketPlaceStandardFlow)
          );
          break;

        case MARKETPLACE_ITEMTYPE_NonTransactionalFlow:
          marketplace.push(
            new MarketPlaceNonTransactionalFlow(
              model as MarketPlaceNonTransactionalFlow
            )
          );
          break;

        case MARKETPLACE_ITEMTYPE_BusinessLogic:
          marketplace.push(
            new MarketPlaceBusinessLogic(model as MarketPlaceBusinessLogic)
          );
          break;

        case MARKETPLACE_ITEMTYPE_DataLogic:
          marketplace.push(
            new MarketPlaceDataLogic(model as MarketPlaceDataLogic)
          );
          break;

        case MARKETPLACE_ITEMTYPE_ChannelReportConfiguration:
          marketplace.push(
            new MarketPlaceChannelReportConfiguration(
              model as MarketPlaceChannelReportConfiguration
            )
          );
          break;

        case MARKETPLACE_ITEMTYPE_StandardProductReport:
          marketplace.push(
            new MarketPlaceStandardProductReport(
              model as MarketPlaceStandardProductReport
            )
          );
          break;

        case MARKETPLACE_ITEMTYPE_StandardProductEmail:
          marketplace.push(
            new MarketPlaceStandardProductEmail(
              model as MarketPlaceStandardProductEmail
            )
          );
          break;

        case MARKETPLACE_ITEMTYPE_ViewEngines:
          marketplace.push(
            new MarketPlaceViewEngine(model as MarketPlaceViewEngine)
          );
          break;

        case MARKETPLACE_ITEMTYPE_Connections:
          marketplace.push(
            new MarketPlaceConnections(model as MarketPlaceConnections)
          );
          break;

        case MARKETPLACE_ITEMTYPE_StandardDictionary:
          marketplace.push(
            new MarketPlaceStandardDictionary(
              model as MarketPlaceStandardDictionary
            )
          );
          break;
      }
    });
    return marketplace;
  }

  private handleError(error: any): Promise<any> {
    console.error('Ocurrió un error', error);
    return Promise.reject(error.message || error);
  }
}



// WEBPACK FOOTER //
// D:/a/1/s/BackOffice/SOC/SxSoc.ClientWeb/src/app/infrastructure/services/marketplace.service.ts